import { useMemo } from "react";
import { QueryKey, UseQueryOptions } from "@tanstack/react-query";

import { graphql } from "lib/generated/app-service-gql";
import { BuyerCategoriesQuery } from "lib/generated/app-service-gql/graphql";
import { useGraphQlQuery } from "../useGraphQlClient";
import { REACT_QUERY_OPTIONS_NEVER_REFETCH } from "../utils";

const BUYER_CATEGORIES_QUERY = graphql(`
  query buyerCategories {
    buyerCategories {
      id
      name
      parentCategoryId
    }
  }
`);

export type BuyerCategory = BuyerCategoriesQuery["buyerCategories"][number];

/** Categories indexed by id — the shape most consumers want. */
export type BuyerCategories = Map<string, BuyerCategory>;

/**
 * The whole buyer-category taxonomy as a flat list. Static reference data, so it's fetched once
 * per session and shared by every consumer.
 */
export function useBuyerCategories(
  options?: UseQueryOptions<BuyerCategoriesQuery, unknown, BuyerCategoriesQuery, QueryKey>,
) {
  const { data, ...rest } = useGraphQlQuery(["buyerCategories"], BUYER_CATEGORIES_QUERY, [{}], {
    ...REACT_QUERY_OPTIONS_NEVER_REFETCH,
    ...options,
  });

  return { data: data?.buyerCategories, ...rest };
}

/**
 * Same query, keyed by category id. Replaces the hand-rolled promise memo the old
 * `BuyerDataProvider` used, and shares one cache entry with `useBuyerCategories` instead of
 * fetching the taxonomy a second time.
 */
export function useBuyerCategoriesById(
  options?: UseQueryOptions<BuyerCategoriesQuery, unknown, BuyerCategoriesQuery, QueryKey>,
) {
  const { data, ...rest } = useBuyerCategories(options);

  const byId = useMemo(
    () => new Map((data ?? []).map((category) => [category.id, category])),
    [data],
  );

  return { data: byId, ...rest };
}
